import { titleCase } from 'change-case'
import { Fragment, useState } from 'react'
import { AssetReference } from '@opallabs/cassini'
import { Button, Input, Select, kuiperClassNames } from '@opallabs/kuiper'
import { getNestedProperty } from '@opallabs/lambda'
import { useSortedOptions } from './hooks/useSortedOptions'
import { useValidFormState, isOptionInvalid } from './hooks/useValidFormState'
import { ScheduledAtDateInput } from './ScheduledAtDateInput'
import { styles } from './styles'
import type { SectionOptionSelectProps, SectionOptionProps } from './types'

interface Props {
  componentName?: string
  deleteDisabled: boolean
  index: number
  onExit: () => void
  onSectionDelete: () => void
  onSectionOptionsSave: (options: SectionOptionProps[]) => void
  readOnly: boolean
  sectionOptions: SectionOptionProps[]
  sections: (AssetReference | string | null)[]
  systemScheduledAt?: string
  timezone?: string
}

export const ModularSectionOptions: React.FC<Props> = ({
  componentName = '',
  deleteDisabled,
  index,
  systemScheduledAt,
  timezone,
  onExit,
  onSectionDelete,
  onSectionOptionsSave,
  readOnly,
  sectionOptions = [],
  sections,
}) => {
  const availablePositions: SectionOptionSelectProps[] = sections.map(
    (section, i) => {
      return { id: i + 1, title: (i + 1).toString() }
    }
  )

  const positionOption: SectionOptionProps = {
    option: 'section position',
    type: 'select',
    content: availablePositions,
    selected: { id: index + 1, title: (index + 1).toString() },
  }

  const allOptions =
    sectionOptions &&
    getNestedProperty(sectionOptions[0], 'option') === 'section position'
      ? sectionOptions
      : [positionOption, ...sectionOptions]
  const [options, setOptions] = useState<SectionOptionProps[]>(allOptions)
  const { sortedOptions, sortedGroupedOptions } = useSortedOptions(
    options,
    sections,
    index
  )

  const formIsValid = useValidFormState(options, sections)

  const onOptionChange = (
    val: SectionOptionSelectProps | string,
    optionName: string
  ): void => {
    // Get dynamic option
    const newOptionValues = [...options]
    const optionIndex = newOptionValues.findIndex(
      (option) => option.option === optionName
    )
    const option = newOptionValues[optionIndex]

    // Make option and form alterations
    if (option.type === 'select') {
      const selectObject = val as SectionOptionSelectProps
      // Clear selection if placeholder option is selected (id=0)
      newOptionValues[optionIndex].selected =
        selectObject.id === 0 ? null : selectObject
    } else {
      newOptionValues[optionIndex].content = val
    }

    // Make option state changes
    setOptions(newOptionValues)
  }

  const getInput = (option: SectionOptionProps): JSX.Element | undefined => {
    if (!option.option) return

    if (
      option.option.toLocaleLowerCase() === 'scheduled at' &&
      systemScheduledAt &&
      timezone
    ) {
      return (
        <ScheduledAtDateInput
          systemScheduledAt={systemScheduledAt}
          timezone={timezone}
          sectionOption={option}
          onChangeSectionOption={(val: string): void =>
            onOptionChange(val, option.option)
          }
        />
      )
    } else if (option.type === 'text' || option.type === 'number') {
      return (
        <Input
          tabIndex={0}
          ariaLabel={`Section ${option.option} Text`}
          theme="light"
          className={kuiperClassNames('modular-section-option')}
          type={option.type}
          value={option.content || ''}
          readOnly={readOnly}
          name={option.option}
          onChange={(val: string): void => onOptionChange(val, option.option)}
        />
      )
    } else if (option.type === 'url') {
      return (
        <Input
          tabIndex={0}
          ariaLabel={`Section ${option.option} Text`}
          placeholder="https://"
          theme="light"
          className={kuiperClassNames('modular-section-option')}
          type={option.type}
          value={option.content || ''}
          readOnly={readOnly}
          name={option.option}
          onChange={(val: string): void => onOptionChange(val, option.option)}
        />
      )
    } else if (option.type === 'select') {
      return (
        <Select<SectionOptionSelectProps>
          ariaLabel={`Section ${option.option} Dropdown`}
          theme="light"
          className={kuiperClassNames('modular-section-option')}
          options={option.content as SectionOptionSelectProps[]}
          disabled={readOnly}
          placeholder="Choose..."
          value={(option.selected as SectionOptionSelectProps) || {}}
          onSelect={(val): void => onOptionChange(val, option.option)}
          selectedComparator={(selectedOption, value): boolean =>
            selectedOption.title === (value as SectionOptionSelectProps).title
          }
        />
      )
    }
  }

  return (
    <div css={styles.sectionOptionsWrapper}>
      <div
        className={kuiperClassNames('modular-section-section-options-header')}
        css={styles.title}
      >
        {componentName} Section Options - {index + 1} of {sections.length}
        <Button
          hideLabelText
          label="Close Section Options Popover"
          icon="close-lg"
          iconOptions={{ height: 14, width: 14 }}
          onClick={onExit}
          type="invisible"
        />
      </div>
      <div css={styles.sectionOptionsWrapperInterior}>
        {sortedOptions &&
          sortedOptions.map((option) => {
            const isValid = !isOptionInvalid(option, sections)
            return (
              <div key={option.option} css={styles.sectionOption}>
                <div css={styles.optionTitle}>
                  {option.option}
                  {option.required && !(option.content || option.selected) && (
                    <div
                      className={kuiperClassNames(
                        'modular-section-required-option'
                      )}
                      css={styles.warning}
                    />
                  )}
                </div>
                <div css={styles.getOptionStyling(option.type || '', isValid)}>
                  {getInput(option)}
                  {!isValid && (
                    <div
                      className={kuiperClassNames(
                        'modular-section-option-error-text'
                      )}
                      css={styles.errorText}
                    >
                      Invalid {option.option.toLocaleLowerCase()}.
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        {sortedGroupedOptions.length > 0 &&
          sortedGroupedOptions.map((group) => (
            <Fragment
              key={`modular-section-option-group-${group[0].group || ''}`}
            >
              <div css={styles.groupHeader}>
                {titleCase(group[0].group || 'group')}
              </div>
              <div css={styles.flexRow}>
                {group.map((option) => {
                  const isValid = !isOptionInvalid(option, sections)
                  return (
                    <div key={option.option} css={styles.groupSectionOption}>
                      <div css={styles.optionTitle}>
                        {option.option}
                        {option.required &&
                          !(option.content || option.selected) && (
                            <div
                              className={kuiperClassNames(
                                'modular-section-required-option'
                              )}
                              css={styles.warning}
                            />
                          )}
                      </div>
                      <div
                        css={styles.getGroupOptionStyling(option.type || '')}
                      >
                        {getInput(option)}
                        {!isValid && (
                          <div
                            className={kuiperClassNames(
                              'modular-section-option-error-text'
                            )}
                            css={styles.errorText}
                          >
                            Invalid {option.option.toLocaleLowerCase()}.
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </Fragment>
          ))}
      </div>
      <div css={styles.footerWrapper}>
        <Button
          ariaLabel="Delete Section"
          className={kuiperClassNames('modular-section-options-delete-section')}
          label="delete"
          type="secondary"
          disabled={deleteDisabled}
          onClick={(): void => onSectionDelete()}
        />
        <Button
          ariaLabel="Save Section Options"
          className={kuiperClassNames('modular-section-options-save')}
          label="save"
          disabled={!formIsValid || readOnly}
          onClick={(): void => onSectionOptionsSave(options)}
        />
      </div>
    </div>
  )
}
